import { useMemo, useState } from 'react';
import { useCustomers } from '../../hooks/useCustomers';
import { useBulkProvisionDevices } from '../../hooks/useDevices';
import type { BulkProvisionResult } from '../../types/device';

interface BulkUploadModalProps {
  onClose: () => void;
  defaultCustomerId?: string;
}

/** Parse pasted/CSV text into a de-duplicated list of serials (first column). */
function parseSerials(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const first = (line.split(',')[0] || '').trim();
    if (!first) continue;
    // Skip a header row like "serial" / "pi_serial".
    if (/serial/i.test(first) && !/^[0-9a-fx]+$/i.test(first)) continue;
    const key = first.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(first);
  }
  return out;
}

function downloadResultsCsv(result: BulkProvisionResult) {
  const header = 'serial,status,reason,device_id,device_name,assigned_ip,activation_code';
  const rows = result.results.map((r) =>
    [r.serial, r.status, r.reason || r.error || '', r.device_id || '', r.device_name || '',
      r.assigned_ip || '', r.activation_code || '']
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(','),
  );
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'securelink-bulk-provision.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const STATUS_STYLES: Record<string, string> = {
  created: 'bg-green-100 text-green-800',
  skipped: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
};

export function BulkUploadModal({ onClose, defaultCustomerId }: BulkUploadModalProps) {
  const { data: customers } = useCustomers();
  const bulk = useBulkProvisionDevices();

  const [customerId, setCustomerId] = useState(defaultCustomerId || '');
  const [namePrefix, setNamePrefix] = useState('');
  const [serialsText, setSerialsText] = useState('');
  const [result, setResult] = useState<BulkProvisionResult | null>(null);

  const serials = useMemo(() => parseSerials(serialsText), [serialsText]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setSerialsText((prev) => (prev ? `${prev}\n${text}` : text));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId || serials.length === 0) return;
    const res = await bulk.mutateAsync({
      customer_id: customerId,
      serials,
      name_prefix: namePrefix || undefined,
    });
    setResult(res);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Bulk provision Pi gateways</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">✕</button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Customer</label>
              <select
                required
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="input"
              >
                <option value="" disabled>Select a customer…</option>
                {customers?.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.organization_name} ({c.device_count}/{c.device_limit})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Device name prefix <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="e.g. Acme-"
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Each device is named <code>{namePrefix || 'Pi-'}&lt;last6ofSerial&gt;</code>.
              </p>
            </div>

            <div>
              <label className="label">Pi serials</label>
              <textarea
                value={serialsText}
                onChange={(e) => setSerialsText(e.target.value)}
                rows={8}
                placeholder={'One serial per line, or paste a CSV (first column used).\n10000000a3cf7a10\n10000000b1de4c22'}
                className="input font-mono text-sm"
              />
              <div className="mt-2 flex items-center justify-between">
                <label className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                  Upload CSV…
                  <input type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
                </label>
                <span className="text-sm text-gray-500">{serials.length} serial(s) parsed</span>
              </div>
            </div>

            {bulk.isError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                {(bulk.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button
                type="submit"
                disabled={!customerId || serials.length === 0 || bulk.isPending}
                className="btn-primary disabled:opacity-50"
              >
                {bulk.isPending ? 'Provisioning…' : `Provision ${serials.length} device(s)`}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Each new device allocates a static Elastic IP and a one-time activation code.
              Serials already registered are skipped, so re-running is safe.
            </p>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span className="font-medium">{result.summary.total} total</span>
              <span className="text-green-700">{result.summary.created} created</span>
              <span className="text-yellow-700">{result.summary.skipped} skipped</span>
              <span className="text-red-700">{result.summary.errors} errors</span>
            </div>

            <div className="card overflow-hidden max-h-80 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Serial</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Static IP</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Activation / note</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {result.results.map((r) => (
                    <tr key={r.serial}>
                      <td className="px-3 py-2 font-mono">{r.serial}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-700">{r.assigned_ip || '—'}</td>
                      <td className="px-3 py-2 font-mono text-gray-700">
                        {r.activation_code || r.reason || r.error || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => downloadResultsCsv(result)} className="btn-secondary">
                Download CSV
              </button>
              <button onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
